/* eslint-disable react-hooks/exhaustive-deps */
import { AlertColor, Box, Button, FormControlLabel, Radio, RadioGroup, Tab, Tabs, Typography } from '@mui/material';
import Form from 'components/Form';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import React, { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { privileges } from 'utils/constants/privileges';
import { useCustomNavigate } from 'utils/hooks/useCustomNavigate';
import { Privilege } from 'utils/redux/reducer/authentication-slice';
import { createDropdownData, fetchIdsFromSelectedDropdownValues } from 'utils/validators/HelperFunctions';
import strings from '../../../../../common/Translation/Translate';
import axiosInstance from '../../../../../utils/axios';
import { routes } from '../../../../../utils/constants/routes';
import { URLS } from '../../../../../utils/constants/urls';
import AddEdit from '../../../CommonPage/AddEdit';
import { AddEditSalesCallsForm, CallingFor } from './AddEditSalesCallsForm';
import dayjs from 'dayjs';
import BookingDiary from 'components/BookingDiary';
import SalesCallFollowUpForm from '../SalesCallFollowUpForm';
import CommentDrawer from 'components/CommentDrawer';
import SalesCallTimelineSection from 'components/SalesCallTimelineSection';
import AddEditCustomer from 'screens/Authenticated/Customer/AddEditCustomer/AddEditCustomer';
import DrawerMenu from 'components/DrawerMenu';

const getContactValue = (value: any) => {
  if (value && typeof value === 'object') {
    return value.label ?? value.value ?? '';
  }
  return value ?? '';
};

const getContactName = (contact: any) =>
  [getContactValue(contact?.title), contact?.firstName, contact?.lastName]
    .filter(value => value !== null && value !== undefined && String(value).trim() !== '')
    .join(' ');

const hasContactDetails = (contact: any) =>
  [
    getContactName(contact),
    contact?.designation,
    getContactValue(contact?.phone),
    contact?.email,
    contact?.loyaltyReference,
    contact?.remarks,
  ].some(value => value !== null && value !== undefined && String(value).trim() !== '');

const createContactsDropdownData = (contacts: any[] = []) =>
  contacts.filter(hasContactDetails).map(contact => ({
    value: contact.id,
    label: contact.name || getContactName(contact) || contact.email || getContactValue(contact.phone),
  }));

const AddEditSalesCalls: FC = () => {
  const IsPrivilege = useSelector(Privilege);
  const { id } = useParams() as any;
  const location = useLocation();
  const navigate: any = useCustomNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [assignees, setAssignees] = useState<any>([]);
  const [followUpModes, setFollowUpModes] = useState<any>([]);
  const [pageResponse, setPageResponse] = useState<any>([]);
  const [companyMasters, setCompanyMasters] = useState<any>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [callingFor, setCallingFor] = useState<CallingFor>(() => {
    const params = new URLSearchParams(location.search);
    const fromQuery = params.get('callingFor');
    if (fromQuery === 'NEW_PROSPECT') return 'NEW_PROSPECT';
    if (fromQuery === 'EXISTING_CONTACT') return 'EXISTING_CONTACT';
    return 'EXISTING_CONTACT';
  });
  const [showDiaryModal, setShowDiaryModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [followUpDrawerOpen, setFollowUpDrawerOpen] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState(0);
  const [commentModalOpen, setCommentModalOpen] = useState(false);

  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({ display: false, severity: null, message: '' });

  const formRef: RefObject<Form | null | undefined> = useRef();

  const closeNotification = (value: boolean) => setMessage({ ...message, display: value });

  const handleCatch = (error: any) => {
    if (error.response) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response.data?.error_description ?? error.response.data.message,
      });
    } else {
      setMessage({
        display: true,
        severity: 'error',
        message: error.message,
      });
    }
  };

  const getInitialResponse = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.fetchSalesCallById(id));

      const inferredCallingFor: CallingFor =
        data?.callingFor === 'NEW_PROSPECT' || data?.callingFor === 'NEW_PROSEPECT'
          ? 'NEW_PROSPECT'
          : data?.callingFor === 'EXISTING_CONTACT'
          ? 'EXISTING_CONTACT'
          : data?.companyMaster
          ? 'EXISTING_CONTACT'
          : data?.prospectName
          ? 'NEW_PROSPECT'
          : '';

      if (inferredCallingFor) {
        setCallingFor(inferredCallingFor);
      }

      // Only load branch/contact dropdowns when editing an existing-contact call
      if (inferredCallingFor === 'EXISTING_CONTACT') {
        const companyMasterId = data?.companyMaster?.value;
        const companyBranchId = data?.companyBranch?.value;

        if (companyMasterId) {
          const branchesDropdown = await axiosInstance(URLS.getCompanyBranchesDropdown(companyMasterId));
          setBranches(createDropdownData(branchesDropdown.data, ['id', 'name']));

          if (companyBranchId) {
            const contactsDropdown = await axiosInstance(URLS.getOrAddBranchContact(companyMasterId, companyBranchId));
            setContacts(createContactsDropdownData(contactsDropdown.data));
          } else {
            setContacts([]);
          }
        } else {
          setBranches([]);
          setContacts([]);
        }
      } else {
        // NEW_PROSPECT – no need for company/branch/contact dropdowns
        setBranches([]);
        setContacts([]);
      }

      setPageResponse({
        ...data,
        companyMaster: data?.companyMaster,
        companyBranch: data?.companyBranch?.value,
        tasks: data.tasks.map((d: any) => ({
          id: d.id,
          createdBy: d.createdBy,
          createdAt: d.createdAt,
          datetime: d.dueDate,
          mode: d.followUpMode,
          assignedTo: d.assignedTo ? d.assignedTo.label : null,
          summary: d.description,
          attachmentUrl: d.attachmentUrl,
          status: d.status,
          completedAt: d.completedAt,
          completionReason: d.completionReason,
        })),
      });
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getInitialResponse();
      }
    })();
  }, [getInitialResponse, id]);

  useEffect(() => {
    (async () => {
      const { data } = await axiosInstance.get(URLS.followUpDropdown);
      setAssignees(createDropdownData(data?.assignees, ['id', 'name']));
      setFollowUpModes(createDropdownData(data?.followUpModes, ['id', 'name']));
    })();
  }, []);

  const pageConfig = {
    title: id ? strings.editSalesManagement : strings.addSalesManagement,
    successMessage: id ? strings.common_update_message : strings.common_save_message,
    addEditApiUrl: id ? URLS.fetchSalesCallById(id) : URLS.addSalesCall,
    initialApi: URLS.fetchSalesCallById(id),
    formModel: AddEditSalesCallsForm(strings, companyMasters, branches, contacts, !!id, followUpModes, callingFor),
    isCustomResponse: true,
    navigateToPage: id ? routes.salesManagement : undefined,
    hideForm: !callingFor,
    saveButtonHide: !callingFor,
  };

  const handleCustomResponse = (custom: any) => {
    const base =
      callingFor === 'EXISTING_CONTACT'
        ? {
            ...custom,
            callingFor: 'EXISTING_CONTACT',
            companyMaster: custom.companyMaster,
            contactedWith: fetchIdsFromSelectedDropdownValues(custom.contactedWith),
          }
        : {
            ...custom,
            callingFor: 'NEW_PROSPECT',
            prospectName: custom.prospectName,
            prospectEmail: custom.prospectEmail,
            prospectPhone: custom.prospectPhone,
            prospectCompanyName: custom.prospectCompanyName,
            prospectDesignation: custom.prospectDesignation,
          };
    return base;
  };

  const getMasterData = useCallback(async () => {
    try {
      setIsLoading(true);
      const { status, data } = await axiosInstance.get(URLS.getCompanyDropdown);
      if (status === 200) {
        const newCompany = [{ name: '+ Add New Company', id: 'new_company' }, ...data];
        setCompanyMasters(createDropdownData(newCompany, ['id', 'name']));
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getMasterData();
  }, [getMasterData]);

  const getBranches = async (companyMasterId: number, companyMaster: any) => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance(URLS.getCompanyBranchesDropdown(companyMasterId));

      if (data.length === 0) {
        setMessage({
          display: true,
          severity: 'error',
          message: 'please add branch first',
        });
      }
      formRef.current?.handleChange('', 'branch', { error: false, message: '' });

      setBranches(createDropdownData(data, ['id', 'name']));
      setPageResponse((prev: any) => ({
        ...prev,
        companyMaster: companyMaster,
        companyBranch: '',
      }));
    } catch (error: any) {
      setBranches([]);
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getContactsList = async (branchId: any) => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance(
        URLS.getOrAddBranchContact(pageResponse.companyMaster?.value || pageResponse.companyMaster, branchId),
      );
      formRef.current?.handleChange('', 'contactedWith', { error: false, message: '' });
      const currentSelectedContacts = pageResponse.contactedWith ?? [];

      const contactsWithAddNew = createContactsDropdownData(data);

      setContacts(contactsWithAddNew);
      setPageResponse((prev: any) => ({
        ...prev,
        companyBranch: branchId,
        // Preserve existing selections when refreshing
        contactedWith: branchId !== prev.companyBranch ? [] : currentSelectedContacts,
      }));
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowDiaryModal(false);
    setShowContactModal(false);
  };

  const handleAddContact = async () => {
    try {
      setShowContactModal(false);
      await getContactsList(pageResponse.companyBranch);
    } catch (e: any) {
      handleCatch(e);
    } finally {
      setIsLoading(false);
    }
  };

  const contactModal = useMemo(() => {
    return (
      <DrawerMenu
        width={'70%'}
        open={showContactModal}
        handleClose={handleModalClose}
        closeButton={true}
        title={strings.customermgmt_title_add}
      >
        <AddEditCustomer
          companyId={pageResponse.companyMaster?.value}
          branchId={pageResponse.companyBranch}
          handleSave={handleAddContact}
        />
      </DrawerMenu>
    );
  }, [handleAddContact, showContactModal]);

  const onChange = (field: string, value: any) => {
    if (field === 'companyMaster' && value) {
      if (value?.value === 'new_company') {
        navigate(routes.addCompanyMasters);
        setPageResponse({ ...pageResponse, companyMaster: '' });
        return false;
      } else {
        getBranches(value?.value || value, value);
        setPageResponse({ ...pageResponse, companyBranch: '', contactedWith: [] });
      }
    } else if (field === 'companyBranch') {
      getContactsList(value);
      setPageResponse({ ...pageResponse, contactedWith: [] });
    } else if (field === 'contactedWith') {
      const newValue = Array.isArray(value) ? value : [];

      if (newValue.some(item => item.value === 'add_new')) {
        setShowContactModal(true);
        setPageResponse((prev: any) => ({
          ...prev,
          contactedWith: (Array.isArray(prev.contactedWith) ? prev.contactedWith : []).filter(
            (id: string) => id !== 'add_new',
          ),
        }));
      } else {
        setPageResponse((prev: any) => ({ ...prev, [field]: newValue }));
      }
    } else {
      setPageResponse((prev: any) => ({ ...prev, [field]: value }));
    }
  };

  const handleFollowUpSuccess = useCallback(async () => {
    await getInitialResponse();
  }, [getInitialResponse]);

  const openCommentModal = () => {
    setCommentModalOpen(true);
  };

  const closeCommentModal = () => {
    setCommentModalOpen(false);
  };
  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      {showContactModal && contactModal}
      {showDiaryModal && (
        <BookingDiary urlParam={pageResponse} open={showDiaryModal} onClose={() => setShowDiaryModal(false)} />
      )}

      <AddEdit
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        handleCustomResponse={handleCustomResponse}
        handlePropsChange={onChange}
        customCallbackResponse={(response: any) => {
          navigate(`${routes.editSalesManagement}/${response.id}`);
        }}
        beforeForm={
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {strings.sales_call_who_are_you_calling}
              </Typography>
              <RadioGroup
                row
                value={callingFor || ''}
                onChange={e => {
                  const v = e.target.value as CallingFor;
                  setCallingFor(v);
                  if (v === 'NEW_PROSPECT') {
                    setPageResponse((prev: any) => ({
                      ...prev,
                      prospectName: '',
                      prospectEmail: '',
                      prospectPhone: '',
                      prospectCompanyName: '',
                      prospectDesignation: '',
                    }));
                  }
                }}
              >
                <FormControlLabel
                  value="EXISTING_CONTACT"
                  control={<Radio />}
                  label={strings.sales_call_existing_contact}
                />
                <FormControlLabel value="NEW_PROSPECT" control={<Radio />} label={strings.sales_call_new_prospect} />
              </RadioGroup>
            </Box>
            {callingFor === 'NEW_PROSPECT' && id && (
              <Button
                variant="outlined"
                onClick={() => {
                  const queryParams = new URLSearchParams();
                  if (pageResponse.prospectCompanyName) {
                    queryParams.append('companyName', pageResponse.prospectCompanyName);
                  }
                  if (pageResponse.prospectEmail) {
                    queryParams.append('email', pageResponse.prospectEmail);
                  }
                  if (pageResponse.prospectPhone) {
                    queryParams.append(
                      'phone',
                      JSON.stringify({ countryCode: 'IN', value: `+91${pageResponse.prospectPhone}` }),
                    );
                  }
                  const url = `${routes.addCompanyMasters}?${queryParams.toString()}`;
                  navigate(url);
                }}
                sx={{ textTransform: 'capitalize', minWidth: '100px', width: 'max-content' }}
              >
                Convert to Company
              </Button>
            )}
          </Box>
        }
        ExtraButtons={() => (
          <Button
            variant="outlined"
            onClick={() => setShowDiaryModal(true)}
            sx={{ textTransform: 'capitalize', minWidth: '100px', width: 'max-content' }}
          >
            {strings.show_diary}
          </Button>
        )}
      />
      {id && IsPrivilege?.includes(privileges.edit_sales_call) && (
        <Box sx={{ my: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            sx={{
              mb: 2,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                borderBottom: '2px solid transparent',
                textTransform: 'capitalize',
                '&.Mui-selected': {
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                },
              },
            }}
          >
            <Tab label="Follow-up / Timeline" />
            <Tab label="Comments" />
          </Tabs>

          {tabValue === 0 && (
            <SalesCallTimelineSection
              items={
                pageResponse.tasks?.map((task: any) => {
                  const createdAt = task.createdAt || task.datetime || task.updatedAt;
                  return {
                    id: task.id,
                    date: createdAt ? dayjs(createdAt).format('DD MMM YYYY') : '-',
                    time: createdAt ? dayjs(createdAt).format('hh:mm A') : '',
                    summary: task.summary ?? '',
                    status: task.status,
                    createdBy: task.createdBy,
                    attachmentUrl: task.attachmentUrl,
                    assignedTo: task.assignedTo,
                  };
                }) || []
              }
              emptyText="No follow-ups available"
              showStatus={true}
              onAddClick={() => setFollowUpDrawerOpen(true)}
              addButtonText="Add Follow-up"
            />
          )}

          {tabValue === 1 && (
            <SalesCallTimelineSection
              items={
                pageResponse.comments?.map((item: any) => {
                  const createdAt = item.createdAt || item.datetime || item.updatedAt;
                  return {
                    id: item.id,
                    date: createdAt ? dayjs(createdAt).format('DD MMM YYYY') : '-',
                    time: createdAt ? dayjs(createdAt).format('hh:mm A') : '',
                    summary: item.comment ?? '',
                    createdBy: item.createdBy,
                    attachmentUrl: item.attachmentUrl,
                  };
                }) || []
              }
              emptyText="No comments available"
              onAddClick={openCommentModal}
              addButtonText="Add Comments"
            />
          )}
        </Box>
      )}
      {id && (
        <SalesCallFollowUpForm
          open={followUpDrawerOpen}
          onClose={() => setFollowUpDrawerOpen(false)}
          salesCallId={Number(id)}
          assignees={assignees}
          followUpModes={followUpModes}
          onSuccess={handleFollowUpSuccess}
        />
      )}
      {commentModalOpen && id && (
        <CommentDrawer
          open={commentModalOpen}
          onClose={closeCommentModal}
          salesCallId={Number(id)}
          onSuccess={getInitialResponse}
        />
      )}
    </>
  );
};

export default AddEditSalesCalls;
